import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@heroui/button";
import { Card, CardBody } from "@heroui/card";
import { Chip } from "@heroui/chip";
import { Link } from "@heroui/link";
import { m } from "framer-motion";

import { subtitle } from "@/components/primitives";
import DefaultLayout from "@/layouts/default";
import ListingCard from "@/components/ListingCard";
import { listings, cities, highSchoolSubjects } from "@/data/listings";
import { slugify } from "@/utils/seo";
import { useListings } from "@/api/useListings";
import { useCities } from "@/api/useCities";
import { useMajorCities } from "@/api/useMajorCities";
import { useSubjects } from "@/api/useSubjects";
import { useBlogs } from "@/api/useBlogs";
import { useSsrRouteData } from "@/ssr/ssrContext";
import ListingCardSkeleton from "@/components/ListingCardSkeleton";
import { useMotionPreferences } from "@/utils/motion";
import { usePopularSubjects } from "@/api/usePopularSubjects";
import { trackActivityEvent } from "@/utils/activityTracking";
import { trackLeadCtaClicked } from "@/utils/googleAdsEvents";
import { preloadRouteByPath } from "@/utils/routePrefetch";
import { TutorSearchBar } from "@/components/TutorSearchBar";

// Animated, below-the-fold marketing demo. Loaded only when it scrolls near the
// viewport, so its code (and framer-motion usage) stays out of the eager home
// bundle and off the initial hydration path.
const ProfLinkerLiveDemo = lazy(() =>
  import("@/components/ProfLinkerLiveDemo").then((mod) => ({
    default: mod.ProfLinkerLiveDemo,
  })),
);

function ProfLinkerDemoPlaceholder() {
  return (
    <div aria-hidden className="editorial-surface min-h-[400px] rounded-2xl" />
  );
}

function ProfLinkerDemoDeferred() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = containerRef.current;

    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [inView]);

  return (
    <div ref={containerRef}>
      {inView ? (
        <Suspense fallback={<ProfLinkerDemoPlaceholder />}>
          <ProfLinkerLiveDemo />
        </Suspense>
      ) : (
        <ProfLinkerDemoPlaceholder />
      )}
    </div>
  );
}

const SCIENCE_SUBJECT_KEYWORDS = [
  "math",
  "physique",
  "chimie",
  "svt",
  "nsi",
  "science",
  "informatique",
  "ingenieur",
  "ingénieur",
];

const isScienceSubject = (subject: string) => {
  const normalized = subject
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

  return SCIENCE_SUBJECT_KEYWORDS.some((keyword) =>
    normalized.includes(keyword),
  );
};

const STEPS = [
  {
    icon: "🔍",
    title: "Cherchez",
    desc: "Filtrez par matière, ville et budget pour trouver le profil qui correspond exactement à vos besoins.",
  },
  {
    icon: "💬",
    title: "Contactez",
    desc: "Échangez directement avec le professeur via notre messagerie intégrée, sans intermédiaire.",
  },
  {
    icon: "🚀",
    title: "Progressez",
    desc: "Démarrez les cours, suivez vos séances et payez en toute sécurité sur la plateforme.",
  },
];

// Rien de chiffré ici tant que ce n'est pas mesuré : ces quatre affirmations
// sont vérifiables par un visiteur en deux clics.
const TRUST_STATS = [
  "Profs sélectionnés un par un",
  "Réponse sous 48h",
  "Sans engagement",
  "Inscription gratuite",
];

// Clés alignées sur le champ `name` des documents `Subject` en base.
// Voir le commentaire de `subjects` dans `@/data/listings`.
const SUBJECT_ICONS: Record<string, string> = {
  Mathématiques: "📐",
  "Physique-Chimie (PC)": "⚗️",
  "Sciences de la vie et de la Terre (SVT)": "🌿",
  "Numérique et Sciences Informatiques (NSI)": "💻",
  "Sciences de l'ingénieur (SI)": "⚙️",
  "Sciences économiques et sociales (SES)": "📊",
  "Histoire-Géographie, Géopolitique et Sciences Politiques (HGGSP)": "🏛️",
  "Humanités, Littérature et Philosophie (HLP)": "🤔",
  "Langues, littératures et cultures étrangères (LLCE)": "🌍",
  "Langues, littératures et cultures étrangères et régionales (LLCER)": "🗺️",
  "Littérature, langues et cultures de l'Antiquité (LLCA)": "📜",
  "Biologie-écologie": "🧬",
  "Éducation physique, pratiques et culture sportives (EPPCS)": "🏃",
  "Arts - Arts du cirque": "🎪",
  "Arts - Arts plastiques (AP)": "🎨",
  "Arts - Cinéma-audiovisuel (CAV)": "🎬",
  "Arts - Danse": "💃",
  "Arts - Histoire des arts (HDA)": "🖼️",
  "Arts - Musique": "🎵",
  "Arts - Théâtre": "🎭",
};

export default function IndexPage() {
  const ssrRouteData = useSsrRouteData();
  const { pageTransition, staggerContainer, fadeInUp, tapScale } =
    useMotionPreferences();
  const { data, loading, error } = useListings({ page: 1, limit: 15 });
  const { data: featuredData } = useListings({
    page: 1,
    limit: 3,
    featured: true,
  });
  const { data: citiesData } = useCities();
  const { data: subjectsData } = useSubjects();
  const { data: popularSubjectsData } = usePopularSubjects({ limit: 12 });
  const listingList = data?.items ?? listings;
  const featuredListings = useMemo(
    () =>
      featuredData?.items ??
      listingList.filter((listing) => listing.isFeatured),
    [featuredData?.items, listingList],
  );
  const cityList = citiesData ?? cities;
  const subjectList = subjectsData ?? highSchoolSubjects;
  const [city, setCity] = useState<string | null>(null);
  const majorCities = useMajorCities(cityList, 12, city);
  const majorSubjects = subjectList.slice(0, 12);
  const popularSubjects = useMemo(() => {
    if (popularSubjectsData && popularSubjectsData.length > 0) {
      return popularSubjectsData;
    }
    const counts = listingList.reduce<Record<string, number>>(
      (acc, listing) => {
        acc[listing.subject] = (acc[listing.subject] ?? 0) + 1;

        return acc;
      },
      {},
    );
    const hasListings = listingList.length > 0;

    return [...subjectList]
      .sort((a, b) => {
        const diff = (counts[b] ?? 0) - (counts[a] ?? 0);

        if (!hasListings) {
          const scienceDiff =
            Number(isScienceSubject(b)) - Number(isScienceSubject(a));

          if (scienceDiff !== 0) return scienceDiff;
        }

        return diff !== 0
          ? diff
          : a.localeCompare(b, "fr", { sensitivity: "base" });
      })
      .slice(0, 12);
  }, [listingList, popularSubjectsData, subjectList]);
  // Le rendu serveur fournit les 3 derniers articles : sans cela la section
  // n'émettait que « Chargement des articles… » dans le HTML, donc aucun lien
  // vers un article pour un robot qui n'exécute pas JavaScript.
  const ssrHomeBlog =
    ssrRouteData.routeType === "home" ? ssrRouteData.home?.blog : null;
  const {
    data: blogData,
    loading: blogLoading,
    error: blogError,
  } = useBlogs({
    initialData: ssrHomeBlog ?? null,
    page: 1,
    limit: 3,
    status: "published",
  });
  const blogPosts = blogData?.items ?? [];
  const [subject, setSubject] = useState<string | null>(null);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const filteredListings = useMemo(() => {
    return listingList.filter((listing) => {
      const matchesSubject = subject
        ? listing.subject.toLowerCase() === subject.toLowerCase()
        : true;
      const matchesCity = city ? listing.city === city : true;
      const matchesKeyword = keyword
        ? `${listing.subject} ${listing.city} ${listing.title} ${listing.ownerName}`
            .toLowerCase()
            .includes(keyword.toLowerCase())
        : true;

      return matchesSubject && matchesCity && matchesKeyword;
    });
  }, [city, keyword, subject, listingList]);

  const handleSearch = () => {
    trackActivityEvent({
      eventType: "search",
      path: "/",
      searchTerm: keyword.trim(),
      filters: {
        city: city ?? "",
        subject: subject ?? "",
      },
    });
    if (subject && city) {
      navigate(`/cours/${slugify(city)}/${slugify(subject)}`);

      return;
    }
    if (subject) {
      navigate(`/cours/${slugify(subject)}`);

      return;
    }
    if (city) {
      navigate(`/cours/${slugify(city)}`);

      return;
    }
    navigate("/tuteurs");
  };

  return (
    <DefaultLayout>
      <m.div animate="show" initial="hidden" variants={pageTransition}>
        {/* ── HERO ─────────────────────────────────────────── */}
        <section className="relative overflow-hidden rounded-3xl mb-10 px-6 py-14 sm:py-20 bg-gradient-to-br from-[#191B36] to-[#FAB519] dark:from-[#FAB519] dark:to-[#191B36]">
          {/* decorative blobs */}
          <div className="pointer-events-none absolute -top-20 -left-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 right-0 h-64 w-64 rounded-full bg-secondary-400/30 blur-3xl" />

          <div className="relative z-10 flex flex-col gap-8 max-w-3xl mx-auto text-center">
            {/* badge */}
            <m.div
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto"
              initial={{ opacity: 0, y: -12 }}
              transition={{ delay: 0.1 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-sm">
                <span className="h-2 w-2 rounded-full bg-success-400 animate-pulse" />
                Des professeurs disponibles près de chez vous
              </span>
            </m.div>

            {/* headline */}
            <m.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 16 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                Le professeur particulier
                <br />
                <span className="text-secondary">qu'il vous faut.</span>
              </h1>
              <p className="mt-4 text-lg text-white/80 max-w-xl mx-auto">
                Des professeurs sélectionnés, des cours sur-mesure et un suivi
                simple pour progresser rapidement, de la 6e aux grandes écoles.
              </p>
            </m.div>

            {/* trust stats */}
            <m.div
              animate={{ opacity: 1 }}
              className="grid grid-cols-2 gap-3 sm:grid-cols-4"
              initial={{ opacity: 0 }}
              transition={{ delay: 0.35 }}
            >
              {TRUST_STATS.map((label) => (
                <div
                  key={label}
                  className="flex items-center justify-center rounded-2xl border border-white/20 bg-white/10 py-3 px-3 backdrop-blur-sm"
                >
                  <p className="text-center text-sm font-semibold text-white">
                    {label}
                  </p>
                </div>
              ))}
            </m.div>

            {/* ── CTA PRIMAIRE : capter la demande avant le catalogue ── */}
            <m.div
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center gap-2"
              initial={{ opacity: 0, y: 8 }}
              transition={{ delay: 0.45 }}
            >
              <Button
                fullWidth
                as={Link}
                className="max-w-md font-semibold"
                color="secondary"
                href="/demande"
                size="lg"
                variant="shadow"
                onFocus={() => preloadRouteByPath("/demande")}
                onMouseEnter={() => preloadRouteByPath("/demande")}
                onPress={() => trackLeadCtaClicked("home_hero")}
              >
                Trouvez votre prof — réponse sous 48h
              </Button>
              <p className="text-sm text-white/70">
                Décrivez votre besoin en 1 minute, un conseiller vous répond.
              </p>
            </m.div>
          </div>
        </section>

        {/* ── SEARCH BAR (action secondaire) ───────────────── */}
        <section className="mt-8 mb-10">
          <p className="mb-3 text-center text-sm text-default-500">
            Ou cherchez vous-même un professeur
          </p>
          <TutorSearchBar
            initialCity={city}
            initialKeyword={keyword}
            initialSubject={subject}
            popularCities={majorCities}
            popularSubjects={majorSubjects}
            variant="hero"
            onChange={(criteria) => {
              setSubject(criteria.subject);
              setCity(criteria.city);
              setKeyword(criteria.keyword);
            }}
            onSubmit={(criteria) => {
              // Sync state then trigger navigation. The bar's onChange
              // already keeps these in sync, but we set explicitly here
              // because handleSearch reads from state.
              setSubject(criteria.subject);
              setCity(criteria.city);
              setKeyword(criteria.keyword);
              // Defer to next tick so state mutation lands first.
              requestAnimationFrame(() => handleSearch());
            }}
          />

          {/* ── ProfLinker CTA (alternative pour qui ne veut pas filtrer) ── */}
          <m.div
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 flex flex-wrap items-center justify-center gap-3"
            initial={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <span className="text-[12px] text-default-500">
              Pas envie de filtrer ?
            </span>
            <Link
              className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#FAB519] to-[#e09010] px-3.5 py-1.5 text-[12.5px] font-semibold text-[#191B36] shadow-md shadow-[#FAB519]/25 transition-all hover:shadow-lg hover:shadow-[#FAB519]/40"
              href="/proflinker"
            >
              <span className="rounded-full bg-white/30 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wide">
                Nouveau
              </span>
              ProfLinker · sélection sur mesure →
            </Link>
          </m.div>
        </section>

        {/* ── HOW IT WORKS ─────────────────────────────────── */}
        <section className="mb-14">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold sm:text-3xl">
              Trouver un prof en 3 étapes
            </h2>
            <p
              className={subtitle({ class: "mt-2 text-base mx-auto max-w-lg" })}
            >
              Simple, rapide et sans engagement.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            {STEPS.map((step, i) => (
              <m.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <Card className="h-full border border-default-200 bg-content1 hover:border-primary/40 transition-colors">
                  <CardBody className="gap-3 py-6 px-5 text-center">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-3xl">
                      {step.icon}
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-white">
                        {i + 1}
                      </span>
                      <p className="text-base font-bold">{step.title}</p>
                    </div>
                    <p className="text-sm text-default-600 leading-relaxed">
                      {step.desc}
                    </p>
                  </CardBody>
                </Card>
              </m.div>
            ))}
          </div>
        </section>

        {/* ── FEATURED TEACHERS ────────────────────────────── */}
        <section className="mb-14">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold sm:text-3xl">
                {subject || city || keyword
                  ? "Résultats de recherche"
                  : "Professeurs en vedette"}
              </h2>
              <p className="text-default-500 mt-1">
                Comparez les profils et contactez directement les professeurs.
              </p>
            </div>
            <m.div
              className="hidden sm:inline-flex shrink-0"
              whileTap={tapScale}
            >
              <Button
                as={Link}
                color="primary"
                href="/tuteurs"
                variant="bordered"
              >
                Voir tous les profils
              </Button>
            </m.div>
          </div>

          <m.div
            animate="show"
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 origin-top-left scale-[0.88]"
            initial="hidden"
            variants={staggerContainer}
          >
            {loading ? (
              <>
                <m.div className="min-w-0" variants={fadeInUp}>
                  <ListingCardSkeleton />
                </m.div>
                <m.div className="min-w-0" variants={fadeInUp}>
                  <ListingCardSkeleton />
                </m.div>
                <m.div className="min-w-0" variants={fadeInUp}>
                  <ListingCardSkeleton />
                </m.div>
              </>
            ) : error ? (
              <m.div className="col-span-full" variants={fadeInUp}>
                <Card className="liquid-glass">
                  <CardBody className="py-8 text-center text-danger-500">
                    {error}
                  </CardBody>
                </Card>
              </m.div>
            ) : (
              (subject || city || keyword
                ? filteredListings
                : featuredListings.length > 0
                  ? featuredListings.slice(0, 3)
                  : listingList.slice(0, 3)
              ).map((listing) => (
                <m.div
                  key={listing._id}
                  className="min-w-0"
                  variants={fadeInUp}
                >
                  <ListingCard
                    listing={listing}
                    variant={listing.isFeatured ? "featured" : "default"}
                  />
                </m.div>
              ))
            )}
          </m.div>

          <div className="mt-6 sm:hidden">
            <Button
              as={Link}
              className="w-full"
              color="primary"
              href="/tuteurs"
              variant="bordered"
            >
              Voir tous les profils
            </Button>
          </div>
        </section>

        {/* ── SUBJECTS & CITIES ────────────────────────────── */}
        <section className="mb-14">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Subjects */}
            <Card className="border border-default-200 bg-content1">
              <CardBody className="gap-4 p-5">
                <div className="flex items-center gap-2">
                  <span className="text-xl">📚</span>
                  <h3 className="text-lg font-bold">Matières populaires</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {popularSubjects.map((subj) => (
                    <Chip
                      key={subj}
                      as={Link}
                      className="cursor-pointer transition-colors hover:bg-primary/20"
                      href={`/cours/${slugify(subj)}`}
                      startContent={
                        <span className="text-base">
                          {SUBJECT_ICONS[subj] ?? "📖"}
                        </span>
                      }
                      variant="flat"
                    >
                      {subj}
                    </Chip>
                  ))}
                </div>
              </CardBody>
            </Card>

            {/* Cities */}
            <Card className="border border-default-200 bg-content1">
              <CardBody className="gap-4 p-5">
                <div className="flex items-center gap-2">
                  <span className="text-xl">📍</span>
                  <h3 className="text-lg font-bold">Villes disponibles</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {majorCities.map((c) => (
                    <Chip
                      key={c}
                      as={Link}
                      className="cursor-pointer transition-colors hover:bg-secondary/20"
                      color="secondary"
                      href={`/cours/${slugify(c)}`}
                      variant="flat"
                    >
                      {c}
                    </Chip>
                  ))}
                </div>
              </CardBody>
            </Card>
          </div>
        </section>

        {/* ── PROFLINKER ───────────────────────────────────── */}
        <section className="mb-14">
          <div className="relative overflow-hidden rounded-3xl border border-default-200/60 bg-gradient-to-br from-[#191B36]/8 via-background to-[#FAB519]/10 p-6 sm:p-10">
            {/* Decorative orbs */}
            <span
              aria-hidden
              className="pointer-events-none absolute -right-32 -top-32 h-72 w-72 rounded-full opacity-[0.18]"
              style={{
                background:
                  "radial-gradient(circle, #FAB519 0%, transparent 70%)",
              }}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full opacity-[0.08]"
              style={{
                background:
                  "radial-gradient(circle, #191B36 0%, transparent 70%)",
              }}
            />

            <div className="relative grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-12">
              <div className="flex flex-col gap-5">
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className="inline-flex h-2 w-2 rounded-full bg-[#FAB519]"
                    style={{
                      boxShadow: "0 0 0 4px rgba(250,181,25,0.18)",
                    }}
                  />
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-default-500">
                    ProfLinker · Sélection sur mesure
                  </p>
                </div>
                <h2 className="text-[34px] font-semibold leading-[1.05] tracking-[-0.02em] text-foreground sm:text-[44px]">
                  Une phrase suffit.
                  <br />
                  <span className="text-[#FAB519]">Le bon prof</span>, et
                  pourquoi lui.
                </h2>
                <p className="max-w-md text-[15px] leading-relaxed text-default-600">
                  Décrivez votre besoin en langage naturel. On vous répond avec
                  le{" "}
                  <span className="font-semibold text-foreground">
                    profil qui correspond
                  </span>
                  , explication incluse, en 30 secondes.
                </p>

                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 border-y border-default-200/40 py-4">
                  <div>
                    <p className="text-[28px] font-semibold leading-none tabular-nums text-foreground">
                      30s
                    </p>
                    <p className="text-[10.5px] uppercase tracking-[0.18em] text-default-500">
                      Pour décider
                    </p>
                  </div>
                  <div>
                    <p className="text-[28px] font-semibold leading-none tabular-nums text-foreground">
                      0€
                    </p>
                    <p className="text-[10.5px] uppercase tracking-[0.18em] text-default-500">
                      3 recherches / sem.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    as={Link}
                    className="font-semibold"
                    color="primary"
                    href="/decouvrir-proflinker"
                    radius="full"
                    size="lg"
                  >
                    Découvrir ProfLinker →
                  </Button>
                  <Button
                    as={Link}
                    className="font-medium"
                    href="/proflinker"
                    radius="full"
                    size="lg"
                    variant="bordered"
                  >
                    Lancer une recherche
                  </Button>
                </div>
              </div>

              <div>
                <ProfLinkerDemoDeferred />
              </div>
            </div>
          </div>
        </section>

        {/* ── BLOG ARTICLES ────────────────────────────────── */}
        <section className="mb-14">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold sm:text-3xl">
                Nos derniers articles
              </h2>
              <p className="text-default-500 mt-1">
                Conseils et actualités pour réussir vos cours particuliers.
              </p>
            </div>
            <m.div
              className="hidden sm:inline-flex shrink-0"
              whileTap={tapScale}
            >
              <Button as={Link} color="primary" href="/blog" variant="light">
                Voir le blog →
              </Button>
            </m.div>
          </div>

          {blogLoading ? (
            <Card className="liquid-glass">
              <CardBody className="py-8 text-center text-default-500">
                Chargement des articles...
              </CardBody>
            </Card>
          ) : blogError ? (
            <Card className="liquid-glass">
              <CardBody className="py-8 text-center text-danger-500">
                {blogError}
              </CardBody>
            </Card>
          ) : blogPosts.length === 0 ? (
            <Card className="liquid-glass">
              <CardBody className="py-8 text-center text-default-500">
                Aucun article disponible pour le moment.
              </CardBody>
            </Card>
          ) : (
            <m.div
              animate="show"
              className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
              initial="hidden"
              variants={staggerContainer}
            >
              {blogPosts.map((post) => (
                <m.div key={post._id} variants={fadeInUp}>
                  <Card className="h-full border border-default-200 bg-content1 hover:border-primary/40 transition-colors">
                    <CardBody className="gap-3 p-5">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-xl">
                        📰
                      </div>
                      <h3 className="text-base font-bold leading-snug">
                        {post.title}
                      </h3>
                      <p className="line-clamp-3 text-sm text-default-600 flex-1">
                        {post.excerpt}
                      </p>
                      <Link
                        className="text-sm font-medium"
                        color="primary"
                        href={`/blog/${post.slug}`}
                      >
                        Lire l'article →
                      </Link>
                    </CardBody>
                  </Card>
                </m.div>
              ))}
            </m.div>
          )}
        </section>

        {/* ── TEACHER CTA BANNER ───────────────────────────── */}
        <section className="mb-10">
          <div className="flex flex-col gap-4 rounded-3xl border border-secondary-200 bg-gradient-to-r from-[#FAB519]/8 to-[#191B36]/5 p-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🎓</span>
                <p className="text-lg font-bold text-foreground">
                  Vous êtes professeur ?
                </p>
              </div>
              <p className="text-sm text-default-700">
                Créez votre annonce <strong>gratuitement</strong> et recevez vos
                premiers élèves.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-3">
              <Button
                as={Link}
                color="primary"
                href="/devenir-professeur"
                size="sm"
                variant="shadow"
              >
                Devenir professeur
              </Button>
            </div>
          </div>
        </section>
      </m.div>
    </DefaultLayout>
  );
}
